import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useNavigate } from 'react-router-dom';
import { Banner, PageLoading } from '../../components/ui';
import Icon from '../../components/Icon';
import type { IconName } from '../../components/Icon';
import { PageHeader } from '../../pages/AppShell';
import type { ExpenseDashboard } from './types';
import { rupees } from './types';

/** First and last day of the current month, YYYY-MM-DD. */
function thisMonth(): { from: string; to: string } {
  const now = new Date();
  const first = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  return { from: first, to: now.toISOString().slice(0, 10) };
}

export default function ExpenseDashboardPage() {
  const navigate = useNavigate();
  const [range, setRange] = useState(thisMonth);
  const [data, setData] = useState<ExpenseDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get<ExpenseDashboard>('/expenses/dashboard', { from: range.from, to: range.to }));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not load the dashboard.');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => { void load(); }, [load]);

  const cards = useMemo(() => data ? ([
    { key: 'created', label: 'Created', icon: 'list' as IconName, tone: 'brand', b: data.created },
    { key: 'pending', label: 'Pending', icon: 'history' as IconName, tone: 'warning', b: data.pending },
    { key: 'approved', label: 'Approved', icon: 'check' as IconName, tone: 'success', b: data.approved },
    { key: 'disbursed', label: 'Disbursed', icon: 'download' as IconName, tone: 'success', b: data.disbursed },
  ]) : [], [data]);

  return (
    <>
      <PageHeader
        title="Expenses"
        subtitle="What your team has claimed, approved and paid out."
        actions={(
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate('/expenses/claims')}>
            Claims & approvals
          </button>
        )}
      />

      <div className="page col gap-16">
        <div className="card card-pad">
          <div className="row gap-12" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label className="col gap-4">
              <span className="small" style={{ fontWeight: 600 }}>From</span>
              <input type="date" className="input" value={range.from}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
            </label>
            <label className="col gap-4">
              <span className="small" style={{ fontWeight: 600 }}>To</span>
              <input type="date" className="input" value={range.to}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
            </label>
            <button type="button" className="btn btn-secondary btn-sm"
              onClick={() => setRange(thisMonth())}>This month</button>
          </div>
        </div>

        {error ? <Banner kind="danger">{error}</Banner> : null}

        {loading || !data ? <PageLoading /> : (
          <>
            <div className="stat-row" style={{ marginBottom: 0 }}>
              {cards.map((c) => (
                <div className="stat" key={c.key}>
                  <div className="stat-icon"><Icon name={c.icon} size={17} /></div>
                  <div style={{ minWidth: 0 }}>
                    <span className="stat-value">{rupees(c.b.total)}</span>
                    <span className="stat-label">{c.label}</span>
                    <span className="stat-label" style={{ color: 'var(--ink-faint)' }}>
                      {c.b.count} claim{c.b.count === 1 ? '' : 's'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="card card-pad">
              <div className="row gap-16" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="small faint">
                  {data.rejected.count > 0
                    ? `${data.rejected.count} rejected (${rupees(data.rejected.total)}) in this period.`
                    : 'No rejected claims in this period.'}
                </span>
                <span className="grow" />
                <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate('/expenses/claims')}>
                  Review claims
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
